// UndoTreeManager - Orchestrates undo/redo operations

import type { DungeonModel } from "../model/dungeon.ts";
import type {
  UndoTree,
  UndoNode,
  OperationType,
  OperationDelta,
  SerializedRoom,
  SerializedCorridor,
} from "./tree.ts";

/**
 * Represents the diff between current state and a target node.
 * Used for visualizing what would change on navigation.
 */
export interface StateDiff {
  addedRooms: SerializedRoom[];
  removedRooms: SerializedRoom[];
  addedCorridors: SerializedCorridor[];
  removedCorridors: SerializedCorridor[];
}
import {
  createUndoTree,
  addNode,
  undo as treeUndo,
  redo as treeRedo,
  navigateToNode,
  getPathToRoot,
  canUndo as treeCanUndo,
  canRedo as treeCanRedo,
  getCurrentNode,
  pruneToNode,
} from "./tree.ts";
import { applyDelta, reverseDelta } from "./deltas.ts";
import { saveTree, loadTree, clearTree } from "./persistence.ts";

/**
 * Callback for when the dungeon state changes due to undo/redo.
 */
export type StateChangeCallback = () => void;

/**
 * Callback for when the tree structure changes.
 */
export type TreeChangeCallback = (tree: UndoTree) => void;

/**
 * Manages undo/redo operations with tree-based history.
 */
export class UndoTreeManager {
  private tree: UndoTree;
  private dungeon: DungeonModel;
  private stateChangeCallbacks: Set<StateChangeCallback> = new Set();
  private treeChangeCallbacks: Set<TreeChangeCallback> = new Set();
  private persistenceKey: string;

  constructor(dungeon: DungeonModel, persistenceKey: string = "hexcrawl-undo-tree") {
    this.dungeon = dungeon;
    this.persistenceKey = persistenceKey;

    // Try to load existing tree from storage
    const savedTree = loadTree(persistenceKey);
    if (savedTree) {
      this.tree = savedTree;
      // Replay from root to current to restore dungeon state
      this.replayToCurrentNode();
    } else {
      // Create new tree with initial state
      this.tree = createUndoTree();
    }
  }

  /**
   * Record an operation (creates a new node in the tree).
   * Call this AFTER the operation has been performed on the dungeon.
   */
  recordOperation(
    operationType: OperationType,
    operationLabel: string,
    delta: OperationDelta
  ): void {
    this.tree = addNode(this.tree, operationType, operationLabel, delta);
    this.save();
    this.notifyTreeChange();
  }

  /**
   * Undo - move to parent node and reverse the delta.
   */
  undo(): boolean {
    if (!treeCanUndo(this.tree)) {
      return false;
    }

    const currentNode = getCurrentNode(this.tree);
    if (!currentNode) return false;

    // Reverse the current node's delta
    reverseDelta(this.dungeon, currentNode.delta);

    // Move to parent
    this.tree = treeUndo(this.tree);
    this.save();
    this.notifyStateChange();
    this.notifyTreeChange();
    return true;
  }

  /**
   * Redo - move to active child node and apply the delta.
   */
  redo(): boolean {
    if (!treeCanRedo(this.tree)) {
      return false;
    }

    // Move to child first to get the node
    this.tree = treeRedo(this.tree);

    const currentNode = getCurrentNode(this.tree);
    if (!currentNode) {
      // Rollback
      this.tree = treeUndo(this.tree);
      return false;
    }

    // Apply the new node's delta
    applyDelta(this.dungeon, currentNode.delta);

    this.save();
    this.notifyStateChange();
    this.notifyTreeChange();
    return true;
  }

  /**
   * Navigate to a specific node (for tree panel clicks).
   */
  navigateTo(nodeId: string): boolean {
    if (!this.tree.nodes.has(nodeId)) {
      return false;
    }

    if (this.tree.currentId === nodeId) {
      return true; // Already there
    }

    // Find paths
    const currentPath = getPathToRoot(this.tree, this.tree.currentId);
    const targetPath = getPathToRoot(this.tree, nodeId);

    // Find common ancestor
    const currentSet = new Set(currentPath);
    let commonAncestorIndex = -1;

    for (let i = 0; i < targetPath.length; i++) {
      if (currentSet.has(targetPath[i])) {
        commonAncestorIndex = i;
        break;
      }
    }

    if (commonAncestorIndex === -1) {
      return false; // No common ancestor (shouldn't happen)
    }

    const commonAncestor = targetPath[commonAncestorIndex];

    // Reverse deltas from current up to (but not including) common ancestor
    const currentAncestorIndex = currentPath.indexOf(commonAncestor);
    for (let i = 0; i < currentAncestorIndex; i++) {
      const node = this.tree.nodes.get(currentPath[i]);
      if (node) {
        reverseDelta(this.dungeon, node.delta);
      }
    }

    // Apply deltas from common ancestor down to target (not including ancestor)
    for (let i = commonAncestorIndex - 1; i >= 0; i--) {
      const node = this.tree.nodes.get(targetPath[i]);
      if (node) {
        applyDelta(this.dungeon, node.delta);
      }
    }

    // Update tree to point to target
    this.tree = navigateToNode(this.tree, nodeId);
    this.save();
    this.notifyStateChange();
    this.notifyTreeChange();
    return true;
  }

  /**
   * Get the current undo tree (for visualization).
   */
  getTree(): UndoTree {
    return this.tree;
  }

  /**
   * Get the current node.
   */
  getCurrentNode(): UndoNode | undefined {
    return getCurrentNode(this.tree);
  }

  /**
   * Check if undo is available.
   */
  canUndo(): boolean {
    return treeCanUndo(this.tree);
  }

  /**
   * Check if redo is available.
   */
  canRedo(): boolean {
    return treeCanRedo(this.tree);
  }

  /**
   * Register callback for state changes.
   */
  onStateChange(callback: StateChangeCallback): void {
    this.stateChangeCallbacks.add(callback);
  }

  /**
   * Remove state change callback.
   */
  offStateChange(callback: StateChangeCallback): void {
    this.stateChangeCallbacks.delete(callback);
  }

  /**
   * Register callback for tree structure changes.
   */
  onTreeChange(callback: TreeChangeCallback): void {
    this.treeChangeCallbacks.add(callback);
  }

  /**
   * Remove tree change callback.
   */
  offTreeChange(callback: TreeChangeCallback): void {
    this.treeChangeCallbacks.delete(callback);
  }

  /**
   * Replay from root to current node to restore dungeon state.
   * Used when loading a saved tree.
   */
  private replayToCurrentNode(): void {
    // Clear dungeon first
    this.dungeon.clear();

    // Get path from root to current
    const path = getPathToRoot(this.tree, this.tree.currentId).reverse();

    // Apply each delta in order (skip root which is initial/no-op)
    for (let i = 1; i < path.length; i++) {
      const node = this.tree.nodes.get(path[i]);
      if (node) {
        applyDelta(this.dungeon, node.delta);
      }
    }
  }

  private notifyStateChange(): void {
    for (const callback of this.stateChangeCallbacks) {
      callback();
    }
  }

  private notifyTreeChange(): void {
    for (const callback of this.treeChangeCallbacks) {
      callback(this.tree);
    }
  }

  private save(): void {
    saveTree(this.tree, this.persistenceKey);
  }

  /**
   * Prune history up to the current node.
   * Makes the current node the new root, discarding all ancestor history.
   * Returns the number of nodes removed.
   */
  pruneHistory(): number {
    const oldCount = this.tree.nodes.size;
    this.tree = pruneToNode(this.tree, this.tree.currentId);
    const removed = oldCount - this.tree.nodes.size;
    this.save();
    this.notifyTreeChange();
    return removed;
  }

  /**
   * Clear the tree and reset to initial state.
   */
  reset(): void {
    this.dungeon.clear();
    this.tree = createUndoTree();
    clearTree(this.persistenceKey);
    this.save();
    this.notifyStateChange();
    this.notifyTreeChange();
  }

  /**
   * Get operation count (excluding initial node).
   */
  getOperationCount(): number {
    return this.tree.nodes.size - 1;
  }

  /**
   * Compute what would change if navigating to a target node.
   * Returns diff without mutating current state.
   */
  computeDiffToNode(nodeId: string): StateDiff | null {
    if (!this.tree.nodes.has(nodeId)) {
      return null;
    }

    if (this.tree.currentId === nodeId) {
      return { addedRooms: [], removedRooms: [], addedCorridors: [], removedCorridors: [] };
    }

    // Find paths from current and target to root
    const currentPath = getPathToRoot(this.tree, this.tree.currentId);
    const targetPath = getPathToRoot(this.tree, nodeId);

    // Find common ancestor
    const currentSet = new Set(currentPath);
    let commonAncestorIndex = -1;
    for (let i = 0; i < targetPath.length; i++) {
      if (currentSet.has(targetPath[i])) {
        commonAncestorIndex = i;
        break;
      }
    }

    if (commonAncestorIndex === -1) {
      return null;
    }

    const commonAncestor = targetPath[commonAncestorIndex];
    const currentAncestorIndex = currentPath.indexOf(commonAncestor);

    // Collect changes from reversing (going up from current to ancestor)
    // When we reverse, "added" becomes "removed" and vice versa
    const addedRooms: SerializedRoom[] = [];
    const removedRooms: SerializedRoom[] = [];
    const addedCorridors: SerializedCorridor[] = [];
    const removedCorridors: SerializedCorridor[] = [];

    // Process nodes being reversed (current -> ancestor, not including ancestor)
    for (let i = 0; i < currentAncestorIndex; i++) {
      const node = this.tree.nodes.get(currentPath[i]);
      if (node) {
        this.collectDeltaChanges(node.delta, removedRooms, addedRooms, removedCorridors, addedCorridors);
      }
    }

    // Process nodes being applied (ancestor -> target, not including ancestor)
    for (let i = commonAncestorIndex - 1; i >= 0; i--) {
      const node = this.tree.nodes.get(targetPath[i]);
      if (node) {
        this.collectDeltaChanges(node.delta, addedRooms, removedRooms, addedCorridors, removedCorridors);
      }
    }

    return { addedRooms, removedRooms, addedCorridors, removedCorridors };
  }

  /**
   * Extract room/corridor changes from a delta.
   * For "apply" direction: added goes to addedRooms, removed goes to removedRooms
   * For "reverse" direction: swap the target arrays
   */
  private collectDeltaChanges(
    delta: OperationDelta,
    addedRooms: SerializedRoom[],
    removedRooms: SerializedRoom[],
    addedCorridors: SerializedCorridor[],
    removedCorridors: SerializedCorridor[]
  ): void {
    switch (delta.type) {
      case "addRoom":
        addedRooms.push(delta.room);
        break;
      case "removeRoom":
        removedRooms.push(delta.room);
        removedCorridors.push(...delta.removedCorridors);
        break;
      case "addCorridor":
        addedCorridors.push(delta.corridor);
        break;
      case "removeCorridor":
        removedCorridors.push(delta.corridor);
        break;
      case "mergeRooms":
        // For merge, show only the NET change to avoid visual overlap
        // Hexes in newRoom but not in any removedRoom = newly drawn area
        // The removed rooms themselves don't visually disappear (they become part of newRoom)
        {
          const removedHexes = new Set<string>();
          for (const room of delta.removedRooms) {
            for (const hex of room.hexes) {
              removedHexes.add(hex);
            }
          }
          // Find hexes that are truly new (in newRoom but not in any removed room)
          const newHexes = delta.newRoom.hexes.filter(h => !removedHexes.has(h));
          if (newHexes.length > 0) {
            // Create synthetic room for the newly added hexes
            addedRooms.push({
              id: `${delta.newRoom.id}-new-hexes`,
              hexes: newHexes,
              label: "",
              userDrawn: true,
              style: { fill: "#f4ece0", stroke: "#5c4a3d" },
            });
          }
          // The merge itself (combining rooms) is shown by corridors being removed
          removedCorridors.push(...delta.removedCorridors);
        }
        break;
      case "deleteScribble":
        removedRooms.push(...delta.rooms);
        removedCorridors.push(...delta.corridors);
        break;
      case "carveRoom":
        // Carving creates a new room from hexes that were part of the original
        // The original room loses those hexes (shown as change to original)
        // For diff purposes, show the carved hexes as "added" (new room appears)
        addedRooms.push(delta.carvedRoom);
        // Also show what was removed from original room
        // Create synthetic room showing the carved-out portion
        {
          const carvedHexes = new Set(delta.carvedRoom.hexes);
          const remainingHexes = delta.originalHexes.filter(h => !carvedHexes.has(h));
          if (remainingHexes.length < delta.originalHexes.length) {
            // Some hexes were carved out - show the original as modified
            // For simplicity, we show the carved portion as "added" (already done above)
          }
        }
        break;
      case "bisectRoom":
        removedRooms.push(delta.originalRoom);
        addedRooms.push(...delta.newRooms);
        removedCorridors.push(...delta.removedCorridors);
        addedCorridors.push(...delta.addedCorridors);
        break;
      case "extendRoom":
        // ExtendRoom adds hexes to an existing room
        // Create a synthetic room to visualize the added hexes
        addedRooms.push({
          id: `${delta.roomId}-extended`,
          hexes: delta.addedHexes,
          label: "",
          userDrawn: true,
          style: { fill: "#f4ece0", stroke: "#5c4a3d" },
        });
        break;
      case "moveRooms":
        // Show old positions as "removed" and new positions as "added"
        // This creates a visual diff showing where rooms moved from/to
        for (const room of delta.roomsBefore) {
          // Old position (before move)
          removedRooms.push(room);
          // New position (after move) - shift all hexes by delta
          const newHexes = room.hexes.map(hexKey => {
            const [q, r] = hexKey.split(",").map(Number);
            return `${q + delta.deltaQ},${r + delta.deltaR}`;
          });
          addedRooms.push({
            ...room,
            id: `${room.id}-moved`,
            hexes: newHexes,
          });
        }
        // Also show corridor changes
        removedCorridors.push(...delta.corridorsBefore);
        addedCorridors.push(...delta.corridorsAfter);
        break;
      case "scaleRooms":
        // Show added hexes (expansion) and removed hexes (contraction)
        for (const change of delta.roomChanges) {
          if (change.addedHexes.length > 0) {
            addedRooms.push({
              id: `${change.roomId}-expanded`,
              hexes: change.addedHexes,
              label: "",
              userDrawn: true,
              style: { fill: "#f4ece0", stroke: "#5c4a3d" },
            });
          }
          if (change.removedHexes.length > 0) {
            removedRooms.push({
              id: `${change.roomId}-contracted`,
              hexes: change.removedHexes,
              label: "",
              userDrawn: true,
              style: { fill: "#f4ece0", stroke: "#5c4a3d" },
            });
          }
        }
        // Show pushed rooms
        for (const pushed of delta.pushedRooms) {
          // Old position
          removedRooms.push(pushed.roomBefore);
          // New position - shift all hexes by delta
          const newHexes = pushed.roomBefore.hexes.map(hexKey => {
            const [q, r] = hexKey.split(",").map(Number);
            return `${q + pushed.deltaQ},${r + pushed.deltaR}`;
          });
          addedRooms.push({
            ...pushed.roomBefore,
            id: `${pushed.roomId}-pushed`,
            hexes: newHexes,
          });
        }
        // Corridor changes
        removedCorridors.push(...delta.corridorsBefore);
        addedCorridors.push(...delta.corridorsAfter);
        break;
      // labelRoom doesn't visually change hexes
      default:
        break;
    }
  }
}
